import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { useQaEvaluations } from '../../hooks/useQaEvaluation.js';
import { ErrorState } from '../../components/feedback/ErrorState';
import { KpiTableSkeleton } from '../../components/feedback/KpiTableSkeleton';
import type { QaLevel } from '../../types/api.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business QA評価一覧ページ。四半期フィルタで評価リスト・統計サマリーを表示し、詳細ページへ遷移
 * @graph-connects useQaEvaluations [calls] QA評価一覧フック呼び出し
 */
export const Route = createFileRoute('/qa/')({
  component: QaPage,
});

/**
 * レベルに応じたバッジを取得
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business QAレベル（senior/middle/junior）に応じた色分けバッジ要素を返却
 * @graph-connects none
 */
function getLevelBadge(level: QaLevel, t: (key: string) => string) {
  switch (level) {
    case 'senior':
      return <Badge variant="success">{t('qa.levels.senior')}</Badge>;
    case 'middle':
      return <Badge variant="info">{t('qa.levels.middle')}</Badge>;
    case 'junior':
      return <Badge variant="default">{t('qa.levels.junior')}</Badge>;
    default:
      return <Badge>{level}</Badge>;
  }
}

/**
 * スコアに応じた色を取得
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business QAスコア（0-10）に応じた色変数（success/info/warning/error）を決定
 * @graph-connects none
 */
function getScoreColor(score: number): string {
  if (score >= 8) return 'var(--color-success)';
  if (score >= 6) return 'var(--color-info)';
  if (score >= 4) return 'var(--color-warning)';
  return 'var(--color-error)';
}

/**
 * 日付をフォーマット
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 日付文字列を日本語ロケール形式（YYYY/MM/DD）にフォーマット
 * @graph-connects none
 */
function formatDate(dateString: string): string {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch (error) {
    // toLocaleDateString が RangeError 等を投げた場合は元文字列のまま表示
    if (!(error instanceof RangeError)) {
      throw error;
    }
    return dateString;
  }
}

/** @graph-connects pilab-api [calls] PILab APIを呼び出す */
function QaPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedQuarter, setSelectedQuarter] = useState<string | undefined>(undefined);

  const { data, isLoading, isError, refetch } = useQaEvaluations({
    quarter: selectedQuarter,
  });

  // 四半期選択オプション
  const quarterOptions = (data?.quarters ?? []).map((q) => ({
    value: q,
    label: q,
  }));

  // 統計計算
  const totalMembers = data?.data.length ?? 0;
  const averageScore =
    totalMembers > 0 && data?.data
      ? (data.data.reduce((sum, e) => sum + e.overallScore, 0) / totalMembers).toFixed(1)
      : '-';

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">{t('qa.title')}</h1>
          <p className="mt-1 text-[var(--color-text-secondary)]">{t('qa.description')}</p>
        </div>
        <KpiTableSkeleton />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">{t('qa.title')}</h1>
          <p className="mt-1 text-[var(--color-text-secondary)]">{t('qa.description')}</p>
        </div>
        <ErrorState
          onRetry={() => {
            void refetch();
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">{t('qa.title')}</h1>
          <p className="mt-1 text-[var(--color-text-secondary)]">{t('qa.description')}</p>
        </div>

        {/* Quarter selector */}
        {quarterOptions.length > 0 && (
          <div className="w-full sm:w-48">
            <Select
              options={quarterOptions}
              value={data?.quarter ?? ''}
              onChange={(e) => {
                setSelectedQuarter(e.target.value);
              }}
              aria-label={t('qa.selectQuarter')}
            />
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card>
          <p className="text-sm text-[var(--color-text-muted)]">{t('qa.totalMembers')}</p>
          <p className="text-2xl font-bold text-[var(--color-text-primary)] mt-1">{totalMembers}</p>
        </Card>
        <Card>
          <p className="text-sm text-[var(--color-text-muted)]">{t('qa.averageScore')}</p>
          <p className="text-2xl font-bold text-[var(--color-text-primary)] mt-1">{averageScore}</p>
        </Card>
      </div>

      {/* Evaluations list */}
      <Card>
        <CardHeader
          title={t('qa.evaluations')}
          description={
            data?.quarter ? t('qa.evaluationsDescription', { quarter: data.quarter }) : undefined
          }
        />
        <CardContent>
          {data?.data.length === 0 ? (
            <div className="text-center py-8 text-[var(--color-text-muted)]">
              {t('qa.noEvaluations')}
            </div>
          ) : (
            <>
              {/* Mobile: Card view */}
              <div className="block md:hidden space-y-3">
                {data?.data.map((evaluation) => (
                  <Link
                    key={evaluation.id}
                    to="/qa/$id"
                    params={{ id: evaluation.id }}
                    className="block p-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] hover:border-[var(--color-border-hover)] transition-colors cursor-pointer"
                  >
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div>
                        <p className="font-medium text-[var(--color-text-primary)]">
                          {evaluation.userName ?? evaluation.userEmail}
                        </p>
                        {evaluation.userName && (
                          <p className="text-xs text-[var(--color-text-muted)]">
                            {evaluation.userEmail}
                          </p>
                        )}
                      </div>
                      {getLevelBadge(evaluation.targetLevel, t)}
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-[var(--color-text-muted)]">{t('qa.overallScore')}</p>
                        <p
                          className="font-semibold text-lg"
                          style={{ color: getScoreColor(evaluation.overallScore) }}
                        >
                          {evaluation.overallScore.toFixed(1)}
                        </p>
                      </div>
                      <div>
                        <p className="text-[var(--color-text-muted)]">{t('qa.evaluatedAt')}</p>
                        <p className="text-[var(--color-text-primary)]">
                          {formatDate(evaluation.evaluatedAt)}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Desktop: Table view */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[var(--color-border)]">
                      <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                        {t('qa.member')}
                      </th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                        {t('qa.overallScore')}
                      </th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                        {t('qa.level')}
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                        {t('qa.evaluatedAt')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.data.map((evaluation) => (
                      <tr
                        key={evaluation.id}
                        className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)] transition-colors cursor-pointer"
                        onClick={() => {
                          void navigate({ to: '/qa/$id', params: { id: evaluation.id } });
                        }}
                      >
                        <td className="py-3 px-4">
                          <div>
                            <p className="text-sm font-medium text-[var(--color-text-primary)]">
                              {evaluation.userName ?? evaluation.userEmail}
                            </p>
                            {evaluation.userName && (
                              <p className="text-xs text-[var(--color-text-muted)]">
                                {evaluation.userEmail}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span
                            className="text-lg font-semibold"
                            style={{ color: getScoreColor(evaluation.overallScore) }}
                          >
                            {evaluation.overallScore.toFixed(1)}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          {getLevelBadge(evaluation.targetLevel, t)}
                        </td>
                        <td className="py-3 px-4 text-sm text-[var(--color-text-secondary)]">
                          {formatDate(evaluation.evaluatedAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
